/**
 * Known harbours and marinas, as a planning overlay.
 *
 * Like webcams ([[webcams.ts]]), **not chart data**, and for the same reason:
 * these positions come from registries rather than from a hydrographic survey,
 * and the listing behind each one is somebody else's website. A harbour marker
 * says "there is a harbour about here and you can read about it there". It says
 * nothing about depth alongside, and the chart under it remains the only thing
 * that does.
 *
 * What it is for: planning. Looking at photographs of a harbour before deciding
 * to enter it is exactly what this app should make easy, and it is the one job
 * a webcam cannot do when the camera is pointing the other way.
 *
 * Most of these are working fishing harbours rather than marinas — Gammons
 * Creek, Advocate, Alder Point — which is what the Nova Scotia coast actually
 * consists of. They are one layer because from a boat looking for somewhere to
 * go, a federal wharf and a yacht club are the same question.
 *
 * Three sources, each within its licence:
 *
 * - **Fisheries and Oceans Canada, Small Craft Harbours** — the federal
 *   registry of fishing harbours. Open Government Licence – Canada.
 * - **OpenStreetMap** — the marinas and yacht clubs DFO does not list. ODbL.
 * - **marinas.com** — the links, where the photographs are. Nothing is copied
 *   or hot-linked; the app links out.
 *
 * `NOVA_SCOTIA_HARBOURS` is generated by `tools/fetch-harbours.mjs`; do not
 * edit it by hand.
 */

import { type HarbourKind, NOVA_SCOTIA_HARBOURS } from './harbours.data';

export type { HarbourKind };

export interface Harbour {
  id: string;
  name: string;
  /** Town, where the source records one. */
  locality: string | null;
  lat: number;
  lon: number;
  kind: HarbourKind;
  source: 'dfo' | 'osm';
  /**
   * True when the link goes to this harbour's own marinas.com listing, matched
   * by name. False when it goes to a search because no single listing matched
   * confidently — "Dartmouth Marina" has no listing of that name, and the
   * nearest candidates are three different facilities.
   */
  listed: boolean;
  url: string;
}

/**
 * Attribution the map must display wherever these positions are drawn.
 *
 * Both source licences require it, so it rides on the harbour source and shows
 * only while the layer is on.
 */
export const HARBOUR_ATTRIBUTION =
  'Harbours: Fisheries and Oceans Canada (Open Government Licence – Canada) '
  + 'and &copy; OpenStreetMap contributors (ODbL). Listings: marinas.com.';

/** How a harbour describes itself in the panel. */
export function describeKind(harbour: Harbour): string {
  if (harbour.kind === 'marina') return 'Marina';
  if (harbour.kind === 'harbour') return 'Recreational harbour';
  return 'Fishing harbour';
}

/** Every known harbour, sorted by name so the order is stable across builds. */
export const HARBOURS: readonly Harbour[] = NOVA_SCOTIA_HARBOURS
  .map((harbour) => ({
    // A slug is stabler across regenerations than an array index. Names are
    // unique within this set — the generator deduplicates on them.
    id: harbour.name.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, ''),
    name: harbour.name,
    locality: harbour.locality,
    lat: harbour.lat,
    lon: harbour.lon,
    kind: harbour.kind,
    source: harbour.source,
    listed: harbour.listed,
    url: harbour.url,
  }))
  .sort((a, b) => a.name.localeCompare(b.name));

/** Look one up by the id carried on a map feature. */
export function harbourById(id: string): Harbour | undefined {
  return HARBOURS.find((harbour) => harbour.id === id);
}

/** GeoJSON for the map layer. Only the id travels; the rest is looked up on tap. */
export function harbourFeatures(harbours: readonly Harbour[] = HARBOURS): GeoJSON.Feature[] {
  return harbours.map((harbour) => ({
    type: 'Feature' as const,
    geometry: { type: 'Point' as const, coordinates: [harbour.lon, harbour.lat] },
    properties: { harbourId: harbour.id, name: harbour.name },
  }));
}
